// src/pages/UniversalPage.tsx

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  useEffect,
  useContext,
} from 'react';
import styled from 'styled-components';

import {
  HEADER_HEIGHT,
  SEPARATOR_HEIGHT,
  MEDIA_BREAKPOINTS,
} from '@/constants/layout';

import { ThemeContext } from '@/theme/ThemeProvider';
import { getBackgroundImagesForEvent } from '@/constants/images';

import {
  type PageOverrides,
  type SeoService,
} from '@/constants/core';
import type { EventType, Region } from '@/constants/geo';

import type { InternalLink } from '@/utils/internalLinks';

import {
  MenuDesktop,
  MenuMobile,
  Section,
} from '@/components';

import Separator from '@/components/StyledComponents/Separator';
import HeroSection from '@/Sections/HeroSection/HeroSection';

import { useI18n } from '@/i18n/LanguageProvider';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

const mediumScreenBreakpoint = parseInt(
  MEDIA_BREAKPOINTS.mediumScreen,
  10
);

// ---------- styled ----------

const SectionsWrapper = styled.div`
  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding-top: ${HEADER_HEIGHT}px;
    background-color: #000;
  }
`;

const SectionFallback = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
`;

const FooterMount = styled.div`
  min-height: 1px;
  background: #000;
`;

// ---------- lazy sections ----------

const StorySection = lazy(
  () => import('@/Sections/StorySection/StorySection')
);
const GallerySection = lazy(
  () => import('@/Sections/GallerySection/GallerySection')
);
const ContactSection = lazy(
  () => import('@/Sections/ContactSection/ContactSection')
);
const InfoSection = lazy(
  () => import('@/Sections/InfoSection/InfoSection')
);
const ReviewsSection = lazy(
  () => import('@/Sections/ReviewsSection/ReviewsSection')
);
const EventPlanningSection = lazy(
  () => import('@/Sections/EventPlanningSection/EventPlanningSection')
);
const Footer = lazy(() => import('@/components/Footer/Footer'));

// ---------- types ----------

export type SectionId =
  | 'home'
  | 'story'
  | 'gallery'
  | 'contact'
  | 'info'
  | 'reviews'
  | 'planning';

type Props = {
  overrides?: PageOverrides;
  internalLinks?: {
    section2?: InternalLink[];
    section4?: InternalLink[];
    footer?: InternalLink[];
  };
  serviceForSection5?: SeoService;
  region?: Region;
  eventType?: EventType;
};

function SpaReadyBridge() {
  useEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  return null;
}

// ==========================================================
// COMPONENT
// ==========================================================

const UniversalPage: React.FC<Props> = ({
  overrides,
  internalLinks,
  region,
  eventType = 'אירוע',
}) => {
  const { i18n } = useI18n();

  const allSections = i18n.MENU_SECTIONS as readonly {
    id: SectionId;
    label: string;
  }[];

  const menuSections = useMemo(
    () =>
      allSections.map((section) =>
        section.id === 'planning' && overrides?.section7?.title
          ? { ...section, label: overrides.section7.title }
          : section
      ),
    [allSections, overrides?.section7?.title]
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loadedSectionIds, setLoadedSectionIds] = useState<Set<SectionId>>(
    () => new Set<SectionId>(['home'])
  );
  const [shouldLoadFooter, setShouldLoadFooter] = useState(false);

  const [selectedSection, setSelectedSection] =
    useState<SectionId>(menuSections[0].id);
  const selectedSectionRef = useRef<SectionId>(
    menuSections[0].id
  );

  const sectionRefs = useRef<
    Map<SectionId, HTMLElement>
  >(new Map());
  const sectionBoundsRef = useRef<
    Map<SectionId, { top: number; bottom: number }>
  >(new Map());
  const footerRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = () =>
    setIsMenuOpen((prev) => !prev);

  // ---------- theme / backgrounds ----------

  const { mode, isDesktop, setEventType } =
    useContext(ThemeContext);

  useEffect(() => {
    setEventType(eventType);
  }, [eventType, setEventType]);

  const markSectionLoaded = useCallback((sectionId: SectionId) => {
    setLoadedSectionIds((prev) => {
      if (prev.has(sectionId)) return prev;
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
  }, []);

  const bgArr = useMemo(() => {
    const size = isDesktop ? 'desktop' : 'mobile';
    return getBackgroundImagesForEvent(eventType)[mode]?.[size] ?? [];
  }, [eventType, isDesktop, mode]);

  const getBgForSection = useCallback(
    (id: SectionId) => {
      const index = allSections.findIndex(
        (s) => s.id === id
      );
      return bgArr[index] ?? '';
    },
    [bgArr, allSections]
  );

  // ---------- content checks ----------

  const hasSection5Content = useMemo(() => {
    const o = overrides?.section5;
    return Boolean(
      o?.title ||
        o?.subtitle ||
        o?.h3Headings?.length ||
        o?.faqs?.length ||
        o?.links?.length
    );
  }, [overrides?.section5]);

  const hasSection6Content = Boolean(
    overrides?.section6?.reviews?.length
  );

  const hasSection7Content = Boolean(
    overrides?.section7?.items?.length
  );

  // ---------- visible sections ----------

  const visibleSections = useMemo(() => {
    let arr = [...menuSections];

    if (!hasSection5Content)
      arr = arr.filter((s) => s.id !== 'info');

    if (!hasSection6Content)
      arr = arr.filter((s) => s.id !== 'reviews');

    if (!hasSection7Content)
      arr = arr.filter((s) => s.id !== 'planning');

    return arr;
  }, [
    menuSections,
    hasSection5Content,
    hasSection6Content,
    hasSection7Content,
  ]);

  useEffect(() => {
    setLoadedSectionIds((prev) => {
      const visibleIds = new Set(visibleSections.map((section) => section.id));
      const next = new Set<SectionId>(['home']);

      prev.forEach((sectionId) => {
        if (visibleIds.has(sectionId)) next.add(sectionId);
      });

      if (next.size === prev.size && Array.from(next).every((sectionId) => prev.has(sectionId))) {
        return prev;
      }

      return next;
    });
  }, [visibleSections]);

  // keep selected valid

  useEffect(() => {
    selectedSectionRef.current = selectedSection;
  }, [selectedSection]);

  useEffect(() => {
    if (
      !visibleSections.some(
        (s) => s.id === selectedSection
      )
    ) {
      const nextSection = visibleSections[0].id;
      selectedSectionRef.current = nextSection;
      setSelectedSection(nextSection);
    }
  }, [visibleSections, selectedSection]);

  // ---------- scrollToSection ----------

  const scrollToSection = useCallback(
    (sectionId: SectionId) => {
      const el =
        sectionRefs.current.get(sectionId);

      if (!el) return;

      markSectionLoaded(sectionId);

      const top =
        el.getBoundingClientRect().top +
        window.pageYOffset;

      const isDesktopNow =
        window.innerWidth >=
        mediumScreenBreakpoint;

      const y =
        top -
        (isDesktopNow
          ? HEADER_HEIGHT +
            SEPARATOR_HEIGHT.largeScreen
          : SEPARATOR_HEIGHT.smallScreen);

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });

      selectedSectionRef.current = sectionId;
      setSelectedSection(sectionId);
    },
    [markSectionLoaded]
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      visibleSections.forEach((section) => markSectionLoaded(section.id));
      setShouldLoadFooter(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const sectionId = (entry.target as HTMLElement).dataset.sectionId as
            | SectionId
            | undefined;

          if (!sectionId) return;
          markSectionLoaded(sectionId);
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.01,
      }
    );

    visibleSections.forEach((section) => {
      if (loadedSectionIds.has(section.id)) return;
      const el = sectionRefs.current.get(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [loadedSectionIds, markSectionLoaded, visibleSections]);

  useEffect(() => {
    if (shouldLoadFooter) return;

    const el = footerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoadFooter(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadFooter(true);
        observer.disconnect();
      },
      {
        rootMargin: '320px 0px',
        threshold: 0,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoadFooter, visibleSections.length]);

  // ---------- scroll spy ----------

  useEffect(() => {
    let raf: number | null = null;
    let measureRaf: number | null = null;
    let initialFrame: number | null = null;
    let postPaintFrame: number | null = null;

    const measureSections = () => {
      measureRaf = null;
      const next = new Map<SectionId, { top: number; bottom: number }>();

      for (const s of visibleSections) {
        const el = sectionRefs.current.get(s.id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        next.set(s.id, {
          top,
          bottom: top + rect.height,
        });
      }

      sectionBoundsRef.current = next;
    };

    const scheduleMeasure = () => {
      if (measureRaf != null) return;
      measureRaf = window.requestAnimationFrame(measureSections);
    };

    const updateSelectedSection = () => {
      raf = null;

      const probeY =
        window.scrollY +
        window.innerHeight / 2;

      let current = selectedSectionRef.current;

      for (const s of visibleSections) {
        const bounds = sectionBoundsRef.current.get(s.id);
        if (!bounds) continue;

        if (probeY >= bounds.top && probeY < bounds.bottom) {
          current = s.id;
          break;
        }
      }

      if (current !== selectedSectionRef.current) {
        selectedSectionRef.current = current;
        setSelectedSection(current);
      }
    };

    const handleScroll = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(updateSelectedSection);
    };

    window.addEventListener(
      'scroll',
      handleScroll,
      { passive: true }
    );
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('orientationchange', scheduleMeasure);

    initialFrame = window.requestAnimationFrame(() => {
      postPaintFrame = window.requestAnimationFrame(() => {
        scheduleMeasure();
        handleScroll();
      });
    });

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);

      if (raf != null) {
        window.cancelAnimationFrame(raf);
      }
      if (measureRaf != null) {
        window.cancelAnimationFrame(measureRaf);
      }
      if (initialFrame != null) {
        window.cancelAnimationFrame(initialFrame);
      }
      if (postPaintFrame != null) {
        window.cancelAnimationFrame(postPaintFrame);
      }
    };
  }, [visibleSections]);

  // ---------- sections render ----------

  const sections = useMemo(
    () =>
      visibleSections.map((s, i) => {
        const id = s.id;

        const comp =
          id === 'home' ? (
            <HeroSection
              overrides={overrides?.section1}
            />
          ) : id === 'story' ? (
            <StorySection
              overrides={overrides?.section2}
              extraLinks={internalLinks?.section2}
            />
          ) : id === 'gallery' ? (
            <GallerySection
              overrides={overrides?.section3}
            />
          ) : id === 'contact' ? (
            <ContactSection
              overrides={overrides?.section4}
              extraLinks={internalLinks?.section4}
              region={region}
            />
          ) : id === 'info' ? (
            <InfoSection
              overrides={overrides?.section5}
            />
          ) : id === 'reviews' ? (
            <ReviewsSection
              overrides={overrides?.section6}
            />
          ) : (
            <EventPlanningSection
              overrides={overrides?.section7}
            />
          );

        const bgUrl = getBgForSection(id);
        const shouldLoadSection = loadedSectionIds.has(id);

        return (
          <React.Fragment key={id}>
            {i === 0 && <Separator />}

            <Section
              ref={(el) => {
                if (el) {
                  (el as HTMLElement).dataset.sectionId = id;
                  sectionRefs.current.set(
                    id,
                    el as HTMLElement
                  );
                } else {
                  sectionRefs.current.delete(id);
                }
              }}
              sectionIndex={i}
              backgroundImageUrl={bgUrl}
              shouldLoad={shouldLoadSection}
            >
              <Suspense
                fallback={<SectionFallback />}
              >
                {comp}
              </Suspense>
            </Section>

            <Separator $reverse={i % 2 === 0} />
          </React.Fragment>
        );
      }),
    [
      visibleSections,
      overrides,
      internalLinks,
      region,
      getBgForSection,
      loadedSectionIds,
    ]
  );

  // ---------- SPA ready ----------

  // ==========================================================

  return (
    <>
      <SpaReadyBridge />

      {isDesktop ? (
        <MenuDesktop
          sections={visibleSections}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          scrollToSection={scrollToSection}
        />
      ) : (
        <MenuMobile
          sections={visibleSections}
          isMenuOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          selectedSection={selectedSection}
          scrollToSection={scrollToSection}
        />
      )}

      <SectionsWrapper>
        {sections}
      </SectionsWrapper>

      <FooterMount ref={footerRef}>
        {shouldLoadFooter && (
          <Suspense fallback={null}>
            <Footer
              footerLinks={internalLinks?.footer}
              region={region}
            />
          </Suspense>
        )}
      </FooterMount>
    </>
  );
};

export default UniversalPage;
